/* global Stripe ApplePaySession*/
import { ApplePayConstants, CartApi } from '@wsb/guac-widget-shared/lib/common/ols-core/shop-bundle';
import { OlsConfigStore } from '@wsb/guac-widget-shared/lib/common/ols-core/core-bundle';
import { AppDispatcher } from '@wsb/guac-widget-shared/lib/common/ols-core/shared-bundle';
import * as ApplePayUtils from './ApplePayApi';
import ApplePayStore from './ApplePayStore';
import { getOrderCompleteUrl } from './getOrderCompleteUrl';

const { dispatchServerAction, dispatchViewAction } = AppDispatcher;

function createOrder(variant, callback, errorCallback) {

  if (ApplePayStore.getState().creatingOrder) {
    return;
  }

  const options = variant.require_phone_number ? { order: { require_phone_number: true } } : null;
  CartApi.createCart(options)
    .then(data => {
      addItemToOrder(data, variant)
        .then(callback)
        .catch(errorCallback);
    })
    .catch(errorCallback);
}

function addItemToOrder(order, variant) {
  const { id, quantity, options } = variant;
  return CartApi.addToOrder(order, id, quantity, options);
}

function dispatchFailure(data) {
  const { error, response } = data;
  if (error.status_code  === 422 && response) {
    const { errors } = response;
    if (errors) {
      if (errors.base) {
        errors.general_error = errors.base;
      }
      const dispatchError = {
        status_code: 422,
        messages: errors,
        message: JSON.stringify({ errors: errors })
      };
      dispatchViewAction({
        type: ApplePayConstants.SESSION_FAILED,
        error: dispatchError
      });
      return;
    }
  }
}

function loadStripe() {
  if (ApplePayStore.getState().stripeWasLoaded) {
    return;
  }

  const script = window.document.createElement('script');
  script.src = 'https://js.stripe.com/v2/';
  script.type = 'text/javascript';
  script.onload = function () {
    dispatchServerAction({
      type: ApplePayConstants.STRIPE_WAS_LOADED
    });
  };
  window.document.body.appendChild(script);
}

class ApplePayActions {
  constructor() {
    this.onShippingContactSelected = this.onShippingContactSelected.bind(this);
    this.onShippingMethodSelected = this.onShippingMethodSelected.bind(this);
    this.handleCreateOrderError = this.handleCreateOrderError.bind(this);
    this.handleCompletePaymentError = this.handleCompletePaymentError.bind(this);
    this.handleShippingContactSelectionError = this.handleShippingContactSelectionError.bind(this);
    this.handleShippingMethodSelectionError = this.handleShippingMethodSelectionError.bind(this);
    this.completeShippingContactSelection = this.completeShippingContactSelection.bind(this);
    this.completeShippingMethodSelection = this.completeShippingMethodSelection.bind(this);
  }

  init(staticContent) {
    loadStripe();
    this.staticContent = staticContent;
  }

  checkAvailability() {
    const { configWasLoaded, stripe_config } = OlsConfigStore.getState();
    const { stripeWasLoaded } = ApplePayStore.getState();

    if (configWasLoaded && stripeWasLoaded) {
      dispatchServerAction({
        type: ApplePayConstants.AVAILABILITY_WAS_CHECKED
      });
      if (stripe_config && stripe_config.with_apple_pay && (typeof Stripe !== 'undefined')) {
        Stripe.setPublishableKey(stripe_config.publishable_key);
        Stripe.applePay.stripeAccount = stripe_config.reference_id;
        Stripe.applePay.checkAvailability(function (available) {
          if (available) {
            dispatchServerAction({
              type: ApplePayConstants.APPLE_PAY_IS_AVAILABLE
            });
          }
        });
      }
    }
  }

  buildTotal() {
    const total = this.order ? this.order.total : this.variant.price * this.variant.quantity;
    return {
      label: OlsConfigStore.getState().store_name,
      amount: total
    };
  }

  buildLineItemsWrapper() {
    if (this.order && this.order.summary_line_items) {
      return this.order.summary_line_items;
    }
    return this.buildLineItems();
  }

  buildLineItems() {
    const lineItems = [];

    if (this.order) {
      lineItems.push({
        label: this.staticContent.subtotal,
        amount: this.order.item_total
      });
      lineItems.push({
        label: this.staticContent.shipping,
        amount: this.order.ship_total
      });

      const adjustments = {};
      for (const adj of this.order.all_adjustments) {
        const key = adj.source_type + '_' + adj.source_id;
        if (adjustments[key]) {
          adjustments[key].amount += 100 * adj.amount;
        } else {
          adjustments[key] = { label: adj.label, amount: 100 * adj.amount };
        }
      }
      for (const key in adjustments) {
        if (adjustments.hasOwnProperty(key)) {
          const adj = adjustments[key];
          lineItems.push({
            label: adj.label,
            amount: Math.round(adj.amount) / 100
          });
        }
      }
    }

    return lineItems;
  }

  buildShippingMethods() {
    const methods = [];

    if (this.order) {
      for (const rate of this.order.shipping_rates) {
        methods.push({
          label: rate.method,
          detail: '',
          identifier: rate.id,
          amount: rate.cost
        });
      }
    }

    return methods;
  }

  completeShippingContactSelection(order) {
    this.order = order;
    this.session.completeShippingContactSelection(
      ApplePaySession.STATUS_SUCCESS,
      this.buildShippingMethods(),
      this.buildTotal(),
      this.buildLineItemsWrapper()
    );
  }

  completeShippingMethodSelection(order) {
    this.order = order;
    this.session.completeShippingMethodSelection(
      ApplePaySession.STATUS_SUCCESS,
      this.buildTotal(),
      this.buildLineItemsWrapper()
    );
  }

  handleCreateOrderError(data) {
    dispatchFailure(data);

    this.session.completeShippingContactSelection(
      ApplePaySession.STATUS_FAILURE,
      [], this.buildTotal(), []
    );
  }

  handleShippingContactSelectionError(data) {
    let errorCode = ApplePaySession.STATUS_INVALID_SHIPPING_POSTAL_ADDRESS;
    const { error, response } = data;
    if (error.status_code  === 422) {
      const { errors } = response;
      for (var key in errors) {
        if (key !== 'email' && !key.match(/^(bill_address|ship_address)\./)) {
          // non-correctable error: get out of apple pay session
          errorCode = ApplePaySession.STATUS_FAILURE;
          dispatchFailure(data);
          break;
        }
      }
    }

    this.session.completeShippingContactSelection(
      errorCode,
      [], this.buildTotal(), []
    );
  }

  handleShippingMethodSelectionError(data) {
    dispatchFailure(data);

    this.session.completeShippingMethodSelection(
      ApplePaySession.STATUS_FAILURE,
      this.buildTotal(), []
    );
  }

  handleCompletePaymentError(data, completion) {
    completion(this.checkErrorStatus(data));
  }

  checkErrorStatus(data) {
    const { error, response } = data;

    if (error.status_code  === 422) {
      const { errors } = response;
      if (errors) {
        if (errors.email
                || errors['ship_address.phone']
                || errors['bill_address.phone']) {
          return ApplePaySession.STATUS_INVALID_SHIPPING_CONTACT;
        } else if (errors['ship_address.firstname']
                || errors['ship_address.lastname']
                || errors['ship_address.address1']
                || errors['ship_address.city']
                || errors['ship_address.state']
                || errors['ship_address.zipcode']) {
          return ApplePaySession.STATUS_INVALID_SHIPPING_POSTAL_ADDRESS;
        } else if (errors['bill_address.firstname']
                || errors['bill_address.lastname']
                || errors['bill_address.address1']
                || errors['bill_address.city']
                || errors['bill_address.state']
                || errors['bill_address.zipcode']) {
          return ApplePaySession.STATUS_INVALID_BILLING_POSTAL_ADDRESS;
        }
      }
    }

    dispatchFailure(data);
    return ApplePaySession.STATUS_FAILURE;
  }

  onShippingContactSelected(e) {
    const updateShipAddress = function () {
      ApplePayUtils.updateCartShipAddress(this.order, e.shippingContact)
        .then(this.completeShippingContactSelection)
        .catch(this.handleShippingContactSelectionError);
    }.bind(this);

    if (this.order) {
      updateShipAddress();
    } else if (this.variant) {
      const successCallback = function (order) {
        this.order = order;
        updateShipAddress();
      }.bind(this);
      const errorCallback = function (error) {
        setTimeout(this.handleCreateOrderError.bind(this, error));
      }.bind(this);

      createOrder(this.variant, successCallback, errorCallback);
    }
  }

  onShippingMethodSelected(e) {
    ApplePayUtils.updateCartShipMethod(this.order, e.shippingMethod)
      .then(this.completeShippingMethodSelection)
      .catch(this.handleShippingMethodSelectionError);
  }

  onCancel() {
    dispatchViewAction({
      type: ApplePayConstants.SESSION_CANCELED
    });
  }

  startSession(params) {
    this.order = params.order;
    this.variant = params.variant;

    const { timeout } = ApplePayStore.getState();
    const { country_iso, currency, stripe_config } = OlsConfigStore.getState();
    const paymentRequest = {
      requiredBillingContactFields: ['postalAddress'],
      requiredShippingContactFields: ['postalAddress', 'email'],
      countryCode: country_iso,
      currencyCode: currency.code,
      shippingMethods: this.buildShippingMethods(),
      lineItems: this.buildLineItemsWrapper(),
      total: this.buildTotal()
    };

    if (this.order) {
      if (this.order.require_phone_number) {
        paymentRequest.requiredShippingContactFields.push('phone');
      }
    } else if (this.variant) {
      if (stripe_config.require_phone_number) {
        this.variant.require_phone_number = true;
        paymentRequest.requiredShippingContactFields.push('phone');
      }
    }

    this.session = Stripe.applePay.buildSession(paymentRequest, function (result, completion) {
      ApplePayUtils.completeOrder(this.order, result.shippingContact, result.token, params.init_page)
        .then(order => {
          completion(ApplePaySession.STATUS_SUCCESS);
          setTimeout(() => {
            window.location.href = getOrderCompleteUrl(order);
          }, timeout || 1000);
        })
        .catch(function (error) {
          this.handleCompletePaymentError(error, completion);
        }.bind(this));
    }.bind(this));

    this.session.onshippingcontactselected = this.onShippingContactSelected;
    this.session.onshippingmethodselected = this.onShippingMethodSelected;
    this.session.oncancel = this.onCancel;

    this.session.begin();

    dispatchViewAction({
      type: ApplePayConstants.SESSION_STARTED
    });
  }
}

export default new ApplePayActions();
